import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { addDays } from "date-fns";
import { useNavigate } from "react-router-dom";

import { Button, Icon, Modal } from "../../common";
import { useGetContactsQuery } from "../../../store/services/contacts";
import { Contact, ContactStatus } from "../../../types/ContactTypes";
import { ContactCard } from "../../cards/ContactCard";
import { selectContacts } from "../../../store/reducers/contacts";
import { AccountType } from "../../../types/UserTypes";
import { selectCurrentUser } from "../../../store/reducers/user";
import { useCreateInvoiceMutation } from "../../../store/services/invoices";
import { ROUTES } from "../../../routes";
import { InvoiceUserType } from "../../../types/InvoiceTypes";
import { DateFormat, formatDate } from "../../../utils/date";
import { NewContactModal } from "../newContactModal";
import { useTriggerToastOnRTKError } from "../../../hooks/useTriggerToastOnRTKError";

type Props = {
  userIs: InvoiceUserType;
  isOpen: boolean;
  onClose: () => void;
};

export const SelectContactModal: FC<Props> = ({ userIs, isOpen, onClose }) => {
  useGetContactsQuery();
  const contacts = useSelector(selectContacts);
  const user = useSelector(selectCurrentUser);
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const navigate = useNavigate();

  const [selectedContact, setSelectedContact] = useState<
    Contact | undefined | null
  >();

  const title =
    userIs === InvoiceUserType.PAYER
      ? "Choose a recipient"
      : "Choose the payer";

  const subtitle =
    userIs === InvoiceUserType.PAYER
      ? "Please select a recipient you are working with"
      : "Please select a payer you are requesting the payment from";

  const [
    createInvoice,
    { data: newInvoice, isLoading, isSuccess, isError, error },
  ] = useCreateInvoiceMutation();

  useTriggerToastOnRTKError(isError, error);

  useEffect(() => {
    if (isSuccess) {
      newInvoice && navigate(ROUTES.INVOICE.replace(":id", newInvoice.id));
    }
  }, [isSuccess, navigate, newInvoice]);

  const onNext = async () => {
    const issuerId =
      userIs === InvoiceUserType.ISSUER ? user?.id : selectedContact?.id;

    const payerId =
      userIs === InvoiceUserType.PAYER ? user?.id : selectedContact?.id;

    await createInvoice({
      issuer_id: issuerId,
      payer_id: payerId,
      issued_at: formatDate(new Date().toString(), DateFormat.yyyy_MM_dd),
      due_at: formatDate(
        addDays(new Date(), 14).toString(),
        DateFormat.yyyy_MM_dd
      ),
    });
  };

  const disableCTA =
    isLoading ||
    selectedContact === undefined ||
    (selectedContact && selectedContact.status !== ContactStatus.Verified);

  return (
    <>
      <Modal title={title} subtitle={subtitle} isOpen={isOpen} close={onClose}>
        <div className="flex flex-col gap-12">
          <div className="flex flex-col gap-4 items-center">
            {(!contacts || contacts.length === 0) && (
              <>You do not have any contacts yet</>
            )}

            {userIs === InvoiceUserType.ISSUER && (
              <ContactCard
                text="Blank Invoice"
                onClick={() => setSelectedContact(null)}
                selected={selectedContact === null}
              />
            )}

            {contacts &&
              contacts.map((contact) => (
                <ContactCard
                  key={contact.id}
                  title={
                    contact.status === ContactStatus.Pending
                      ? contact.email
                      : contact.account_type === AccountType.BUSINESS
                        ? "Company"
                        : "Individual"
                  }
                  text={contact.contact_name}
                  status={contact.status}
                  onClick={() => setSelectedContact(contact)}
                  selected={contact.id === selectedContact?.id}
                />
              ))}

            <Button
              variant={Button.variant.SECONDARY}
              className="w-full"
              onClick={() => setShowNewContactForm(true)}
              contentClassName="w-full"
            >
              <div className="flex justify-between">
                <span>Invite new contact</span>

                <Icon
                  icon="Add"
                  size={24}
                  className="text-gray-500"
                  disableFill
                />
              </div>
            </Button>
          </div>

          {selectedContact &&
            selectedContact.status !== ContactStatus.Verified && (
              <div className="flex flex-row items-center w-full rounded-lg p-4 justify-between border border-brown-300 bg-brown-100">
                <div className="flex flex-row items-center">
                  <div className="ml-6 flex flex-col">
                    <h6 className="text-base text-gray-500 font-bold">
                      The recipient is not yet verified
                    </h6>

                    <div className="font-semibold text-base text-gray-500">
                      Please wait for the contact to get verified to send or
                      receive payments
                    </div>
                  </div>
                </div>
              </div>
            )}

          <div className="flex flex-col gap-6">
            <Button
              variant={Button.variant.PRIMARY}
              className="w-full"
              onClick={onNext}
              disabled={disableCTA || false}
            >
              Next
            </Button>
          </div>
        </div>
      </Modal>

      {showNewContactForm && (
        <NewContactModal
          isOpen={showNewContactForm}
          onClose={() => setShowNewContactForm(false)}
        />
      )}
    </>
  );
};
